import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import Seo from '../components/Seo';
import { PAGES } from '../seo/meta.mjs';

const Design = () => {
  const turbineTypes = [
    {
      id: 'francis',
      name: 'Francis Turbine',
      description: 'Medium to high head applications with steady flow. Optimal for most hydro power configurations.',
      specs: 'Head: 10-350m | Efficiency: 85-95% | Flow: Variable',
      image: 'https://images.unsplash.com/photo-1709131121922-028189a2c984?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxoeWRybyUyMHBvd2VyJTIwcGxhbnQlMjBkYW0lMjBzY2VuaWN8ZW58MHx8fHwxNzcwMjkyNTY1fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      id: 'kaplan',
      name: 'Kaplan Turbine',
      description: 'Low head, high flow applications. Variable blade pitch for efficiency across flow ranges.',
      specs: 'Head: 2-40m | Efficiency: 80-90% | Flow: High',
      image: 'https://images.unsplash.com/photo-1769697148689-4ae92b0d8848?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxoeWRybyUyMHBvd2VyJTIwcGxhbnQlMjBkYW0lMjBzY2VuaWN8ZW58MHx8fHwxNzcwMjkyNTY1fDA&ixlib=rb-4.1.0&q=85'
    },
    {
      id: 'pelton',
      name: 'Pelton Turbine',
      description: 'High head, low flow applications. Impulse turbine with precision jet and bucket design.',
      specs: 'Head: 50-1300m | Efficiency: 85-92% | Flow: Low',
      image: 'https://images.unsplash.com/photo-1760343043912-80a4687c7cea?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxkYXJrJTIwYmx1ZSUyMHdhdGVyJTIwYWJzdHJhY3QlMjB0ZXh0dXJlfGVufDB8fHx8MTc3MDI5MjU4Nnww&ixlib=rb-4.1.0&q=85'
    },
    {
      id: 'crossflow',
      name: 'Crossflow Turbine',
      description: 'Versatile turbine for small to medium hydro. Simple design with good part-load efficiency.',
      specs: 'Head: 2-200m | Efficiency: 75-85% | Flow: Medium',
      image: 'https://images.unsplash.com/photo-1763507229005-c91a64090c48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxkYXJrJTIwYmx1ZSUyMHdhdGVyJTIwYWJzdHJhY3QlMjB0ZXh0dXJlfGVufDB8fHx8MTc3MDI5MjU4Nnww&ixlib=rb-4.1.0&q=85'
    }
  ];

  const embeddedSystems = [
    {
      name: 'IoT Smart Hydro Project Monitoring System',
      description: 'Real-time remote monitoring with cloud connectivity. Track performance metrics, alarms, and historical data from anywhere.',
      features: ['Remote SCADA access', 'Mobile app integration', 'Predictive maintenance alerts', 'Data analytics dashboard']
    },
    {
      name: 'Governor',
      description: 'Precision speed and load control for stable grid synchronization. Microprocessor-based with PID control.',
      features: ['Fast response time', 'Load rejection handling', 'Manual/Auto modes', 'Communication protocols']
    },
    {
      name: 'Electronic Load Controller',
      description: 'Frequency regulation through ballast load management. Essential for islanded micro-grid operations.',
      features: ['Instant load switching', 'Thyristor-based control', 'Multiple load banks', 'Frequency stabilization']
    }
  ];

  const controlPanelTypes = [
    {
      name: 'Hydro Power Control Panels',
      description: 'Complete control and protection for hydro plants',
      applications: ['Main Control Panel', 'Excitation Panel', 'Synchronization Panel', 'Governor Panel']
    },
    {
      name: 'Industrial Automation',
      description: 'Process control and motor control centers',
      applications: ['PLC Panels', 'VFD Panels', 'Motor Control Centers', 'Process Control']
    },
    {
      name: 'Power Distribution',
      description: 'Electrical distribution and protection systems',
      applications: ['LT Panels', 'HT Panels', 'PCC Panels', 'APFC Panels']
    },
    {
      name: 'Renewable Energy',
      description: 'Solar and wind power control systems',
      applications: ['Solar Inverter Panels', 'Wind Control Panels', 'Battery Management', 'Grid Tie Systems']
    }
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="design-page">
      <Seo {...PAGES.design} />
      <section className="py-24 bg-slate-950" data-testid="design-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.h1
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="font-heading font-light text-5xl md:text-7xl tracking-tighter text-white mb-6 text-center"
            data-testid="design-title"
          >
            Design <span className="text-ocean-500">Excellence</span>
          </motion.h1>
          <motion.p
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto text-center"
          >
            Engineering precision in turbine selection and control panel design
          </motion.p>
        </div>
      </section>

      <section className="py-24 bg-slate-900" data-testid="turbines-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-white mb-16 text-center">
            Turbine Types & Selection
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {turbineTypes.map((turbine, index) => (
              <Link
                key={turbine.id}
                to={`/design/turbine/${turbine.id}`}
                data-testid={`turbine-card-${turbine.id}`}
                className="group"
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-slate-900/50 border border-white/5 overflow-hidden hover:border-ocean-500/50 transition-all cursor-pointer"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={turbine.image}
                      alt={turbine.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8">
                    <h3 className="font-heading text-2xl font-semibold text-white mb-4 flex items-center justify-between">
                      {turbine.name}
                      <ArrowRight className="text-ocean-500 group-hover:translate-x-2 transition-transform" size={20} />
                    </h3>
                    <p className="text-slate-400 leading-relaxed mb-6">{turbine.description}</p>
                    <div className="bg-slate-800/50 border border-white/10 p-4">
                      <p className="font-mono text-sm text-ocean-400">{turbine.specs}</p>
                    </div>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-950" data-testid="embedded-systems-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-white mb-16 text-center">
            Embedded Smart Systems
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {embeddedSystems.map((system, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`embedded-system-${index}`}
                className="bg-slate-900/50 border border-white/5 p-8 hover:border-ocean-500/30 transition-all"
              >
                <h3 className="font-heading text-xl font-semibold text-white mb-4">{system.name}</h3>
                <p className="text-slate-400 leading-relaxed mb-6">{system.description}</p>
                <ul className="space-y-2">
                  {system.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-slate-400 text-sm">
                      <span className="text-ocean-500 mt-1">•</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900" data-testid="control-panels-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-white mb-16 text-center">
            Control Panel Solutions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {controlPanelTypes.map((panel, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                data-testid={`control-panel-${index}`}
                className="bg-slate-900/50 border border-white/5 p-6 hover:border-ocean-500/30 transition-all group"
              >
                <h3 className="font-heading text-lg font-semibold text-white mb-3 group-hover:text-ocean-500 transition-colors">
                  {panel.name}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-4">{panel.description}</p>
                <ul className="space-y-1">
                  {panel.applications.map((app, idx) => (
                    <li key={idx} className="text-slate-500 text-xs">
                      • {app}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-950" data-testid="methodology-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-white mb-12 text-center">
            Engineering Methodology
          </h2>
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-slate-900/50 border border-white/5 p-8">
              <h3 className="font-heading text-xl font-semibold text-white mb-4">
                Site Assessment & Selection
              </h3>
              <p className="text-slate-400 leading-relaxed">
                Detailed analysis of head, flow, site conditions, and grid requirements to determine
                optimal turbine type and configuration.
              </p>
            </div>
            <div className="bg-slate-900/50 border border-white/5 p-8">
              <h3 className="font-heading text-xl font-semibold text-white mb-4">
                Control System Architecture
              </h3>
              <p className="text-slate-400 leading-relaxed">
                Comprehensive protection coordination, logic design, and system integration ensuring
                safe and reliable operation.
              </p>
            </div>
            <div className="bg-slate-900/50 border border-white/5 p-8">
              <h3 className="font-heading text-xl font-semibold text-white mb-4">
                Panel Layout & Fabrication
              </h3>
              <p className="text-slate-400 leading-relaxed">
                3D panel modeling, component selection, and detailed manufacturing drawings optimized
                for ease of installation and maintenance.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Design;
